import { Children, isValidElement } from "react";
import type { ComponentType, ReactNode } from "react";
import type { Components } from "react-markdown";
import { cn } from "@/core/utils/utils";
import { DROPPED, WORD } from "./diff-marks";
import {
  DROP_CLOSE,
  DROP_OPEN,
  MARK_CLOSE,
  MARK_OPEN,
  type DiffBlock,
} from "./rules-diff";

/**
 * Wraps the words the diff marked, after markdown has been parsed.
 *
 * The marks travel as zero-width sentinels inside the text, so they survive
 * parsing and can be applied wherever the word ended up — inside a bold run, a
 * list item or a table cell — without ever putting HTML into the document.
 */
const split = (
  text: string,
  open: string,
  close: string,
  className: string,
  remainder: (text: string) => ReactNode = (text) => text,
  /** Read aloud before the marked words, when colour is all that says which. */
  label = "",
) =>
  text.split(open).map((chunk, index) => {
    if (index === 0) return remainder(chunk);
    const [hit, ...rest] = chunk.split(close);
    return (
      <span key={index}>
        {!!label && <span className="sr-only">{label} </span>}
        <mark className={cn("rounded-sm bg-transparent px-px", className)}>
          {hit}
        </mark>
        {remainder(rest.join(close))}
      </span>
    );
  });

/**
 * Marks the searched words themselves, wherever markdown put them.
 *
 * The first attempt tinted the whole passage a match fell in, and a passage
 * here is a section of the document, not a line: searching a common word
 * washed half the page blue and said nothing about where the word was. The
 * grid highlights the cell, and a word is this document's cell.
 *
 * Same two strengths as everywhere else: every hit takes the lighter blue, and
 * the hits in the passage the reader has stepped to take the stronger one.
 */
export const HIT = "bg-blue-20";
export const HIT_ACTIVE = "bg-blue-40";

const findIn = (
  nodes: ReactNode,
  needle: string,
  className: string,
): ReactNode =>
  Children.map(nodes, (node) => {
    if (typeof node === "string") {
      const lower = node.toLowerCase();
      if (!lower.includes(needle)) return node;
      const parts: ReactNode[] = [];
      let from = 0;
      for (
        let at = lower.indexOf(needle);
        at !== -1;
        at = lower.indexOf(needle, from)
      ) {
        if (at > from) parts.push(node.slice(from, at));
        parts.push(
          <mark key={at} className={cn("rounded-sm px-px", className)}>
            {node.slice(at, at + needle.length)}
          </mark>,
        );
        from = at + needle.length;
      }
      parts.push(node.slice(from));
      return <>{parts}</>;
    }
    if (isValidElement<{ children?: ReactNode }>(node) && node.props.children)
      return {
        ...node,
        props: {
          ...node.props,
          children: findIn(node.props.children, needle, className),
        },
      };
    return node;
  });

/** Every string in a subtree, so a mark can be looked for across its parts. */
const textOf = (nodes: ReactNode): string => {
  let text = "";
  Children.forEach(nodes, (node) => {
    if (typeof node === "string") text += node;
    else if (
      isValidElement<{ children?: ReactNode }>(node) &&
      node.props.children
    )
      text += textOf(node.props.children);
  });
  return text;
};

const marked = (
  nodes: ReactNode,
  className: string,
  /**
   * True inside a changed table cell, which holds the old value and the new
   * one side by side with only their colour saying which is which. Read aloud
   * that was "20 25". Everywhere else the passage's own sign already says
   * removed or added, so the labels would be said twice.
   */
  paired?: boolean,
): ReactNode => {
  // Decided over the whole cell, before its parts are walked. A bold value
  // puts the old and the new marks inside two different `strong` elements, so
  // neither string node can see the other and each read as a lone change.
  const both =
    paired ??
    (() => {
      const text = textOf(nodes);
      return text.includes(DROP_OPEN) && text.includes(MARK_OPEN);
    })();
  return Children.map(nodes, (node) => {
    if (typeof node === "string") {
      if (node.includes(DROP_OPEN))
        return split(
          node,
          DROP_OPEN,
          DROP_CLOSE,
          DROPPED,
          (text) => marked(text, className, both),
          both ? "Previously" : "",
        );
      if (!node.includes(MARK_OPEN)) return node;
      return split(
        node,
        MARK_OPEN,
        MARK_CLOSE,
        className,
        undefined,
        both ? "Now" : "",
      );
    }
    if (isValidElement<{ children?: ReactNode }>(node) && node.props.children)
      return {
        ...node,
        props: {
          ...node.props,
          children: marked(node.props.children, className, both),
        },
      };
    return node;
  });
};

// Every element that can hold the document's own text. The shared components
// map only overrides tables, so the marks have to be applied at each of these
// rather than by wrapping what that map happens to define.
const TEXT_TAGS = [
  "p",
  "li",
  "td",
  "th",
  "blockquote",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
] as const;

// A wide table still has to scroll in its own box rather than widen the pane.
const BASE_COMPONENTS: Components = {
  table: ({ node: _node, ...props }) => (
    <div className="overflow-x-auto">
      <table {...props} />
    </div>
  ),
};

export const componentsFor = (
  kind: DiffBlock["kind"],
  // Lowercased, and empty when nothing is being searched.
  needle: string,
  hit: string,
) => {
  const diffed = kind !== "same";
  if (!diffed && !needle) return BASE_COMPONENTS;
  const wrapped: Record<string, ComponentType<Record<string, unknown>>> = {};
  for (const tag of TEXT_TAGS) {
    const Tag = tag as keyof JSX.IntrinsicElements;
    wrapped[tag] = ({ node: _node, children, ...rest }) => {
      // Diff marks first, then the search over the result: the search has to
      // see the words as the reader does, including the ones the diff has
      // already wrapped.
      const body = diffed
        ? marked(children as ReactNode, WORD[kind])
        : (children as ReactNode);
      return <Tag {...rest}>{needle ? findIn(body, needle, hit) : body}</Tag>;
    };
  }
  return { ...BASE_COMPONENTS, ...wrapped };
};
